/**
 * 促销活动 API 模块（固定字段直取版）
 * 只使用后端当前固定字段：
 * - SalesType
 * - BeginTime
 * - EndTime
 * - MaxSales
 * - ID
 * - SalesSubType
 */

import { API_ENDPOINTS, fetchWithRetry, getApiUrl } from '../config/api';

const LIST_OPTION = 1;
// 活动类型固定规则（用户明确指定）：
// - 100: 金币促销
// - 101: 钻石促销
// - 104: 金币+钻石共同促销
const SALE_ACTIVITY_IDS = [100, 101, 104];
const COIN_SALES_TYPES = [100, 104];
const GEM_SALES_TYPES = [101, 104];

const toPositiveNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? num : 0;
};

const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const buildActivityListHeaders = (user) => {
    if (!user) throw new Error('User not authenticated');
    if (!user.gameToken) throw new Error('gameToken is empty');

    return {
        'Content-Type': 'application/json',
        Gametoken: user.gameToken,
        Fpid: user.id,
        Playerid: user.playerid,
    };
};

const getCurrentTimestamp = () => Math.floor(Date.now() / 1000);

const extractActivityList = (data) => {
    // 固定字段（用户明确指定）：
    // 活动列表只从 data.TypeCActivityList.List 读取。
    // 不再尝试任何其他路径，避免字段猜测导致逻辑跑偏。
    return Array.isArray(data?.TypeCActivityList?.List) ? data.TypeCActivityList.List : [];
};

const normalizeActivity = (activity) => {
    return {
        ...activity,
        SalesType: toNumber(activity?.SalesType),
        BeginTime: toNumber(activity?.BeginTime),
        EndTime: toNumber(activity?.EndTime),
        MaxSales: toNumber(activity?.MaxSales),
        ID: toNumber(activity?.ID),
        SalesSubType: String(activity?.SalesSubType || ''),
    };
};

const isActivityOpen = (activity) => {
    const begin = toNumber(activity?.BeginTime);
    const end = toNumber(activity?.EndTime);
    const now = getCurrentTimestamp();
    if (begin <= 0 || end <= 0) return false;
    return begin <= now && now <= end;
};

const isCoinActivity = (activity) => {
    const salesType = toNumber(activity?.SalesType);
    // 固定按 SalesType 判断，不再按 SalesSubType 做推断。
    return COIN_SALES_TYPES.includes(salesType);
};

const isGemActivity = (activity) => {
    const salesType = toNumber(activity?.SalesType);
    // 固定按 SalesType 判断，不再按 SalesSubType 做推断。
    return GEM_SALES_TYPES.includes(salesType);
};

const getSaleRatio = (activity) => toPositiveNumber(activity?.MaxSales);

const buildTimePatch = (activityData) => {
    const todayEnd = toPositiveNumber(activityData?.EndOfDay);
    const serverNow = toPositiveNumber(activityData?.Now);
    return {
        ...(todayEnd > 0 ? { todayEnd, today_end: todayEnd } : {}),
        ...(serverNow > 0 ? { serverNow, serverTimeOffsetMs: Date.now() - serverNow * 1000 } : {}),
    };
};

export const fetchActivityList = async (user, options = {}) => {
    const headers = buildActivityListHeaders(user);
    const body = { ActivityIDs: SALE_ACTIVITY_IDS };
    if (options.option !== undefined) body.Option = options.option;

    const response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.GET_TYPEC_ACTIVITY_LIST), {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch activity list: HTTP ${response.status}`);
    }

    const data = await response.json();
    if (data.Code !== undefined && data.Code !== 0 && data.Code !== 200) {
        throw new Error(`API error: Code ${data.Code}, Message: ${data.Message || 'Unknown'}`);
    }

    const list = extractActivityList(data).map(normalizeActivity);

    return { ...data, list };
};

export const getCoinSaleActivity = async (user) => {
    const { list = [] } = await fetchActivityList(user, { option: LIST_OPTION });
    // 金币活动开启判断：
    // 只要活动在有效时间窗口内且类型命中金币活动类型，就视为“活动开启”
    const activity = list.find((item) => isActivityOpen(item) && isCoinActivity(item));
    if (!activity) return null;
    const ratio = getSaleRatio(activity);
    // 说明：MaxSales 可能为 0（后端仍判定活动开启），这里不再用 ratio>0 作为是否开启条件。
    return { ...activity, ratio, percentage: Math.round(ratio * 100), TypeCID: String(activity.ID) };
};

export const getGemSaleActivity = async (user) => {
    const { list = [] } = await fetchActivityList(user, { option: LIST_OPTION });
    // 钻石活动开启判断：
    // 只要活动在有效时间窗口内且类型命中钻石活动类型，就视为“活动开启”
    const activity = list.find((item) => isActivityOpen(item) && isGemActivity(item));
    if (!activity) return null;
    const ratio = getSaleRatio(activity);
    // 说明：MaxSales 可能为 0（后端仍判定活动开启），这里不再用 ratio>0 作为是否开启条件。
    return { ...activity, ratio, percentage: Math.round(ratio * 100), TypeCID: String(activity.ID) };
};

export const getAllSaleActivities = async (user) => {
    const activityData = await fetchActivityList(user, { option: LIST_OPTION });
    const { list = [] } = activityData;

    const activityInspectRows = list.map((activity) => {
        const ratio = getSaleRatio(activity);
        return {
            ID: activity.ID,
            SalesType: activity.SalesType,
            SalesSubType: activity.SalesSubType,
            BeginTime: activity.BeginTime,
            EndTime: activity.EndTime,
            MaxSales: activity.MaxSales,
            isOpen: isActivityOpen(activity),
            isCoin: isCoinActivity(activity),
            isGem: isGemActivity(activity),
            ratio,
            raw: activity,
        };
    });

    let coinRatioTotal = 0;
    let gemRatioTotal = 0;
    let coinSource = null;
    let gemSource = null;
    let coinActive = false;
    let gemActive = false;

    for (const activity of list) {
        if (!isActivityOpen(activity)) continue;
        const ratio = getSaleRatio(activity);

        if (isCoinActivity(activity)) {
            coinActive = true;
            if (ratio > 0) coinRatioTotal += ratio;
            if (!coinSource) coinSource = activity;
        }

        if (isGemActivity(activity)) {
            gemActive = true;
            if (ratio > 0) gemRatioTotal += ratio;
            if (!gemSource) gemSource = activity;
        }
    }

    // 活动对象用于“是否显示活动UI”和“下单带活动ID”
    // 即使 ratio=0，只要活动处于开启状态也要返回活动对象。
    const coinSale = coinActive && coinSource ? { ...coinSource, ratio: coinRatioTotal, percentage: Math.round(coinRatioTotal * 100), TypeCID: String(coinSource.ID) } : null;

    const gemSale = gemActive && gemSource ? { ...gemSource, ratio: gemRatioTotal, percentage: Math.round(gemRatioTotal * 100), TypeCID: String(gemSource.ID) } : null;

    return {
        coinSale,
        gemSale,
        timePatch: buildTimePatch(activityData),
    };
};

export default {
    fetchActivityList,
    getCoinSaleActivity,
    getGemSaleActivity,
    getAllSaleActivities,
};
